import React, { useState } from "react";
import styled from "styled-components";
import { FaCheck as CheckIcon, FaCopy as CopyIcon } from "react-icons/fa";
import { Contract, NewContract } from "@models";
import {
	SmallText,
	LargeText,
	FlexCol,
	Icon,
	Tooltip,
	Badges,
	FlexRow,
} from "@components";
import { formatDate } from "../../../../components/forms/datePickerUtils";
import { ProductIcon } from "@components";
import { getDateFlags, Currency, formatCategoryName } from "@util";
import { isNewContract } from "../util";
import ContractControlsDropdown from "./ContractControlsDropdown";
import { NO_CORRESPONDING_BUDGET_MESSAGE } from "../util";
import { useStaticData } from "@hooks";
import { colors } from "@theme/theming";

type Props = {
	contract: Contract | NewContract;
	hasCorrespondingBudget: boolean;
	expanded?: boolean;
	className?: string;
};

export function ContractCardHeader(props: Props) {
	const { contract, hasCorrespondingBudget } = props;
	const [copied, setCopied] = useState(false);
	const [copiedExternalId, setCopiedExternalId] = useState(false);

	const { isExpired, isInFuture } = getDateFlags(
		contract.startDate,
		contract.endDate
	);

	const isNew = isNewContract(contract);
	const { getProductFor, getCategory } = useStaticData();
	const product = getProductFor(contract.category);
	const category = getCategory(contract.category);

	const copyContractId = (e: React.MouseEvent<HTMLDivElement>) => {
		e.stopPropagation();
		if (!("contractId" in contract)) return;

		navigator.clipboard
			.writeText(contract.contractId)
			.then(() => {
				setCopied(true);
				setTimeout(() => {
					setCopied(false);
				}, 1000);
			})
			.catch(() => console.log("Error copying"));
	};

	const copyExternalId = (e: React.MouseEvent<HTMLDivElement>) => {
		e.stopPropagation();
		if (!contract.externalId) return;

		navigator.clipboard
			.writeText(contract.externalId)
			.then(() => {
				setCopiedExternalId(true);
				setTimeout(() => {
					setCopiedExternalId(false);
				}, 1000);
			})
			.catch(() => console.log("Error copying"));
	};

	return (
		<>
			<FlexCol
				css={`
					min-width: 8em;
				`}
			>
				<Amount title="Betrag">
					{Currency.format(contract.amount, { hideZeroDecimals: true })}
				</Amount>
			</FlexCol>
			<FlexCol
				css={`
					min-width: 2em;
					font-size: 2em;
				`}
			>
				<ProductIcon productId={product.id} />
			</FlexCol>
			<FlexCol
				css={`
					flex: 0 1 20em;
					min-width: 0;
				`}
			>
				<LargeText>{formatCategoryName(category)}</LargeText>
				<SmallText translate="no">{contract.name}</SmallText>
			</FlexCol>
			<FlexCol
				css={`
					flex: 0 1 10em;
					min-width: 0;
				`}
			>
				<SmallText>
					<span title="von">von: {formatDate(contract.startDate)}</span>
				</SmallText>
				<SmallText title="bis">
					bis: {contract.endDate ? formatDate(contract.endDate) : "unbefristet"}
				</SmallText>
			</FlexCol>
			{isNew && <Badges.New />}
			{isExpired && <Badges.Expired />}
			{isInFuture && <Badges.InFuture />}
			{!hasCorrespondingBudget && !props.expanded && (
				<FlexCol
					css={`
						min-width: 10em;
					`}
				>
					<Tooltip
						id="no-budget-warning"
						tooltip={NO_CORRESPONDING_BUDGET_MESSAGE}
					>
						<div style={{ color: colors.caution.c300 }}>
							<span
								css={`
									font-size: 2em;
								`}
							>
								<Icon.Warning /> Kein Budget!
							</span>
						</div>
					</Tooltip>
				</FlexCol>
			)}
			<Spacer />
			{"contractId" in contract && (
				<ContractIdColumn>
					{contract.externalId && (
						<ContractIdRow>
							<ContractIdText className="small">
								<ExternalIdLabel>Externe ID:</ExternalIdLabel>{" "}
								<span className="text-muted">{contract.externalId}</span>
							</ContractIdText>
							<div style={{ flexShrink: 0 }}>
								<CopyIconWrapper
									onClick={copyExternalId}
									title="Externe ID kopieren"
								>
									{copiedExternalId ? <CopiedCheckIcon /> : <CopyIcon />}
								</CopyIconWrapper>
							</div>
						</ContractIdRow>
					)}
					<ContractIdRow>
						<ContractIdText className="text-muted small">
							{contract.contractId.replaceAll("-", "-\u200B")}
						</ContractIdText>
						<div style={{ flexShrink: 0 }}>
							<CopyIconWrapper onClick={copyContractId} title="Kopieren">
								{copied ? <CopiedCheckIcon /> : <CopyIcon />}
							</CopyIconWrapper>
						</div>
					</ContractIdRow>
				</ContractIdColumn>
			)}
			{/* If more options are added in the dropdwon then this condition should be applied only to delete contract option */}
			{"contractId" in contract && (
				<ContractControlsDropdown contract={contract} />
			)}
		</>
	);
}

const Amount = styled.div`
	font-size: 1.5rem;
`;

const Spacer = styled.div`
	flex: 1 1 0;
	min-width: 0;
`;

const ContractIdColumn = styled(FlexCol)`
	margin: 0px 16px 0px 8px;
	justify-content: center;
	align-items: stretch;
	gap: 4px;
	flex: 0 1 auto;
	min-width: 0;
`;

const ContractIdRow = styled(FlexRow)`
	align-items: center;
	justify-content: flex-end;
	gap: 8px;
	flex-wrap: nowrap;
	width: 100%;
`;

const ContractIdText = styled.div`
	word-break: break-word;
	flex-shrink: 1;
	min-width: 0;
`;

const ExternalIdLabel = styled.span`
	color: inherit;
`;

const CopyIconWrapper = styled.div`
	transition: color 0.3s, border-color 0.3s;
	border: solid 1px ${colors.gray.g300};
	border-radius: 5px;
	display: flex;
	align-items: center;
	justify-content: center;
	height: 30px;
	width: 30px;
	color: ${colors.gray.g300};
	&:hover {
		color: ${colors.gray.g400};
		border-color: ${colors.gray.g400};
	}
`;

const CopiedCheckIcon = styled(CheckIcon)`
	color: ${colors.success.s300};
	animation: iconAnimation 0.3s ease-in-out;
	@keyframes iconAnimation {
		0% {
			transform: scale(0.5);
		}
		70% {
			transform: scale(1.1);
		}
		100% {
			transform: scale(1);
		}
	}
`;
